import React, { useState } from "react";
import SEO from "../seo/SEO";
import { Mail, HelpCircle, Send, CheckCircle } from "lucide-react";

export default function ContactUs() {
  const [formData, setFormData] = useState({ name: "", email: "", subject: "", message: "" });
  const [isSent, setIsSent] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSent(true);
    setFormData({ name: "", email: "", subject: "", message: "" });
  };

  return (
    <div className="space-y-16 py-12">
      <SEO 
        title="Contact Us - USMoneyAI Support & Feedback" 
        description="Get in touch with the USMoneyAI support team. Submit feature requests, report formula errors, or ask dynamic API integration questions."
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Contact", url: "/contact" }
        ]}
      />

      <div className="text-center space-y-4 max-w-2xl mx-auto">
        <div className="flex justify-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-cyan-500/25 bg-cyan-950/30 text-xs font-mono text-cyan-400">
            <Mail className="w-3.5 h-3.5" />
            Support Center
          </div>
        </div>
        <h1 className="text-4xl font-display font-medium text-white tracking-tight sm:text-5xl">
          Contact Us
        </h1>
        <p className="text-slate-400 text-sm font-light leading-relaxed">
          Have a question, found an issue with a calculator, or would like to suggest a new financial tool? We'd love to hear from you. Send us a message and we'll do our best to help.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-5xl mx-auto items-start">
        {/* Contact Info */}
        <div className="space-y-8">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Direct Communication</h3>
            <p className="text-sm text-slate-400 font-light leading-relaxed">
              Our automated simulation platform runs globally, but our core team of developers and financial analysts responds to custom validation queries regularly.
            </p>
          </div>

          <div className="bg-[#040914] border border-slate-900 rounded-2xl p-6 flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-cyan-950/40 border border-cyan-850/50 flex items-center justify-center text-cyan-400 shrink-0">
              <Mail className="w-5 h-5" />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white">General &amp; Partnership Support</h4>
              <p className="text-xs text-slate-400 font-light mt-1">For API distribution queries, formula partnerships, or user feedback.</p>
              <a href="mailto:support@usmoneyai.com" className="text-xs font-mono font-medium text-cyan-400 mt-2 hover:underline block">
                support@usmoneyai.com
              </a>
            </div>
          </div>

          <div className="bg-[#040914] border border-slate-900 rounded-2xl p-6 flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-cyan-950/40 border border-cyan-850/50 flex items-center justify-center text-cyan-400 shrink-0">
              <HelpCircle className="w-5 h-5" />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white font-sans text-left">Math Accuracy Verification</h4>
              <p className="text-xs text-slate-400 font-light mt-1">If you notice discrepancy in mortgage tax amortization calculations or compounding intervals.</p>
              <span className="text-[10px] text-emerald-400 font-mono mt-2 block">
                ● Checked against NIST mathematical standards
              </span>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-slate-950/60 border border-slate-800 rounded-2xl p-6 sm:p-8 shadow-xl">
          {isSent ? (
            <div className="text-center py-12 space-y-4">
              <div className="w-12 h-12 rounded-full bg-emerald-950/30 border border-emerald-800/60 text-emerald-400 flex items-center justify-center mx-auto">
                <CheckCircle className="w-6 h-6" />
              </div>
              <h4 className="text-lg font-semibold text-white">Query Submitted Successfully</h4>
              <p className="text-xs text-slate-400 font-light leading-relaxed max-w-xs mx-auto">
                Thank you! Your feedback has been secured. Our mathematical verification staff will review your query shortly.
              </p>
              <button 
                onClick={() => setIsSent(false)}
                className="px-4 py-2 bg-slate-900 border border-slate-800 hover:bg-slate-800 text-slate-200 text-xs font-medium rounded-xl transition-colors cursor-pointer"
              >
                Send Another Message
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label className="text-xs text-slate-300 font-semibold text-left block">Your Name</label>
                <input 
                  type="text" 
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Doe"
                  className="w-full bg-[#030712] border border-slate-800 py-2.5 px-3 rounded-xl text-xs text-slate-100 outline-none focus:border-cyan-500"
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs text-slate-300 font-semibold text-left block">Email Address</label>
                <input 
                  type="email" 
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@example.com"
                  className="w-full bg-[#030712] border border-slate-800 py-2.5 px-3 rounded-xl text-xs text-slate-100 outline-none focus:border-cyan-500"
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs text-slate-300 font-semibold text-left block">Subject</label>
                <input 
                  type="text" 
                  required
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="Calculator feedback"
                  className="w-full bg-[#030712] border border-slate-800 py-2.5 px-3 rounded-xl text-xs text-slate-100 outline-none focus:border-cyan-500"
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs text-slate-300 font-semibold text-left block">Message</label>
                <textarea 
                  required
                  rows={4}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Detail your request or feedback here..."
                  className="w-full bg-[#030712] border border-slate-800 py-2.5 px-3 rounded-xl text-xs text-slate-100 outline-none focus:border-cyan-500 font-sans"
                />
              </div>

              <button 
                type="submit"
                className="w-full py-3 bg-cyan-950/60 border border-cyan-800 hover:bg-cyan-950 text-cyan-400 font-semibold rounded-xl text-xs transition-colors flex items-center justify-center gap-2 cursor-pointer"
              >
                <Send className="w-3.5 h-3.5" />
                <span>Submit Secure Session Ticket</span>
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
